/**
 * One POST to Make.com after a paid order (same moment as GTM purchase).
 * Deduped via sessionStorage so polling / reload in the same tab does not repeat the request.
 *
 * Override URL: window.__MAKE_PURCHASE_WEBHOOK_URL__ (optional).
 * Debug: localStorage.setItem('CV_DEBUG_MAKE_WEBHOOK', '1')
 */

const DEFAULT_WEBHOOK_URL =
    'https://hook.eu2.make.com/lvrl6hjeuj1ziqxw4y07stqoe5l5fq75';
const DEDUP_PREFIX = 'cv_make_purchase_webhook_';
const ATTRIB_KEY = 'cv_attribution_v1';

function webhookUrl() {
    if (typeof window === 'undefined') return DEFAULT_WEBHOOK_URL;
    return (
        (window.__MAKE_PURCHASE_WEBHOOK_URL__ &&
            String(window.__MAKE_PURCHASE_WEBHOOK_URL__).trim()) ||
        DEFAULT_WEBHOOK_URL
    );
}

function debug(msg, extra) {
    try {
        if (typeof localStorage === 'undefined') return;
        if (localStorage.getItem('CV_DEBUG_MAKE_WEBHOOK') !== '1') return;
        console.info('[CV Make webhook]', msg, extra);
    } catch {
        //
    }
}

function isGatewayPaid(payment) {
    if (!payment) return false;
    const api = String(payment.api_status || '').trim().toLowerCase();
    if (api === 'paid') return true;
    const paidAt = payment.paid_at;
    if (paidAt != null && String(paidAt).trim() !== '') return true;
    return String(payment.status || '').trim().toLowerCase() === 'paid';
}

function readAttributionSnap() {
    try {
        const raw = localStorage.getItem(ATTRIB_KEY);
        if (!raw) return null;
        const j = JSON.parse(raw);
        return j && typeof j === 'object' ? j : null;
    } catch {
        return null;
    }
}

function readGclid() {
    try {
        const direct = localStorage.getItem('gclid');
        if (direct) return direct.trim();
        const snap = readAttributionSnap();
        const p = snap?.utm_params;
        return String(p?.gclid ?? p?.GCLID ?? '').trim();
    } catch {
        return '';
    }
}

function readMsclkid() {
    try {
        const direct = localStorage.getItem('fromMicrosoftAds');
        if (direct) return direct.trim();
        const snap = readAttributionSnap();
        return String(snap?.utm_params?.msclkid ?? '').trim();
    } catch {
        return '';
    }
}

function readClientId() {
    try {
        const direct = localStorage.getItem('client_id');
        if (direct) return direct.trim();
        const snap = readAttributionSnap();
        return String(snap?.client_id ?? '').trim();
    } catch {
        return '';
    }
}

function readSessionId() {
    try {
        const direct = localStorage.getItem('client_sess_id');
        if (direct) return direct.trim();
        const snap = readAttributionSnap();
        return String(snap?.client_sess_id ?? '').trim();
    } catch {
        return '';
    }
}

export { readClientId, readGclid, readMsclkid, readSessionId };

/** Sum line `amount`; matches PHP subtotal across lines. */
function ordersSubtotalString(lines) {
    if (!Array.isArray(lines) || !lines.length) return '';
    let sum = 0;
    for (const line of lines) {
        sum += Number.parseFloat(String(line.amount ?? '0')) || 0;
    }
    return sum.toFixed(2);
}

export async function sha256HexEmail(email) {
    const norm = String(email || '').trim();
    if (!norm) return '';
    if (typeof crypto === 'undefined' || !crypto.subtle) {
        debug('skip email hash: Web Crypto API unavailable');
        return '';
    }
    const enc = new TextEncoder().encode(norm);
    const buf = await crypto.subtle.digest('SHA-256', enc);
    return Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}

/**
 * Matches legacy PHP associative keys sent to Make.com.
 * @param {Record<string, any>} d Root `data` from `/orders/get`
 */
export async function maybeSendMakePurchaseWebhook(d) {
    if (typeof window === 'undefined' || !d || typeof d !== 'object') return false;

    const payment = d.payment;
    if (!isGatewayPaid(payment)) {
        debug('skip: not paid');
        return false;
    }

    const lines = Array.isArray(d.orders) ? d.orders : [];
    if (!lines.length) {
        debug('skip: no order lines');
        return false;
    }

    const txnId = d.id != null ? String(d.id) : String(payment.id ?? '');
    const dedupeKey = `${DEDUP_PREFIX}${payment.id}_${txnId}_${payment.api_id ?? ''}`;

    try {
        if (sessionStorage.getItem(dedupeKey)) return false;
    } catch {
        return false;
    }

    /** Optimistic dedupe — cleared if fetch fails so the next poll can retry. */
    try {
        sessionStorage.setItem(dedupeKey, '1');
    } catch {
        return false;
    }

    /** Same key shape as your PHP snippet (spaces preserved for Make scenarios). */
    const payload = {
        'transaction id': txnId,
        value: ordersSubtotalString(lines),
        gclid: readGclid(),
        'Currency Code': payment.currency?.code || 'EUR',
        cid: readClientId(),
        sid: readSessionId(),
        msclkid: readMsclkid(),
        email: await sha256HexEmail(d.customer?.email),
    };

    const url = webhookUrl();

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
            mode: 'cors',
            credentials: 'omit',
        });
        if (!res.ok) {
            debug('HTTP error', res.status);
            try {
                sessionStorage.removeItem(dedupeKey);
            } catch {
                //
            }
            return false;
        }
        debug('sent', payload);
        return true;
    } catch (e) {
        debug('network error', e);
        try {
            sessionStorage.removeItem(dedupeKey);
        } catch {
            //
        }
        return false;
    }
}
